/**
 * Cross-platform video download service
 * iOS: uses Web Share API with File to show native share sheet (user can tap "Save Video" → Photos).
 * Fallback on iOS: open in a new tab so user can Share → Save Video.
 * Android/Desktop: standard <a download> flow as before.
 */

class VideoDownloadService {
  constructor() {
    // Enhanced iOS detection (all iOS browsers use WebKit)
    const ua = navigator.userAgent || "";
    const platform = (navigator.platform || "").toLowerCase();

    this.isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (platform.includes("mac") && navigator.maxTouchPoints > 1);

    this.isAndroid = /Android/.test(ua);
    this.isMobile = this.isIOS || this.isAndroid;

    // Safari heuristic (all iOS browsers are Safari/WebKit under the hood)
    this.isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);

    // iOS "variants" (still WebKit)
    this.isIOSSafari = this.isIOS && this.isSafari;
    this.isIOSChrome = this.isIOS && /CriOS/.test(ua);
    this.isIOSFirefox = this.isIOS && /FxiOS/.test(ua);
  }

  /**
   * Main entry
   */
  async downloadVideo(videoId, onProgress = null, onError = null) {
    try {
      onProgress?.("Preparing download...");

      const downloadUrl = `/api/download/video/${videoId}`;
      const filename = `recap-video-${videoId}.mp4`;
      const mime = "video/mp4";

      onProgress?.("Starting download...");

      if (this.isMobile) {
        return await this.downloadMobile(downloadUrl, filename, mime, onProgress, onError);
      } else {
        return await this.downloadDesktop(downloadUrl, filename, mime, "attachment", onProgress, onError);
      }
    } catch (err) {
      console.error("Download error:", err);
      onError?.(err?.message || "Download failed");
      return false;
    }
  }

  /**
   * Mobile
   */
  async downloadMobile(downloadUrl, filename, contentType, onProgress, onError) {
    try {
      if (this.isIOS) {
        return await this.downloadIOS(downloadUrl, filename, contentType, onProgress, onError);
      } else {
        return await this.downloadDirect(downloadUrl, filename, onProgress, onError);
      }
    } catch (err) {
      console.error("Mobile download error:", err);
      onError?.(err?.message || "Download failed");
      return false;
    }
  }

  /**
   * iOS strategy:
   * 1) Try Web Share API with File (best UX → allows "Save Video" to Photos)
   * 2) Fallback: open new tab so user can Share → Save Video
   * 3) Last-resort: <a download> (often ignored by iOS)
   */
  async downloadIOS(downloadUrl, filename, contentType, onProgress, onError) {
    // 1) Web Share API with files
    const didShare = await this.tryIOSWebShare(downloadUrl, filename, contentType, onProgress);
    if (didShare) return true;

    // 2) Fallback: open URL so the iOS Share sheet can be used manually
    const didOpen = this.tryOpenNewTab(downloadUrl, onProgress);
    if (didOpen) return true;

    // 3) Last-resort direct <a download> (iOS may ignore)
    const didAnchor = this.tryAnchorDownload(downloadUrl, filename, onProgress);
    if (didAnchor) return true;

    onError?.("Download failed. Please try again.");
    return false;
  }

  /**
   * iOS: Web Share with File (Level 2). If supported, shows native share sheet with "Save Video".
   * NOTE: Requires proper CORS headers and that the response is fetchable from the browser.
   */
  async tryIOSWebShare(downloadUrl, filename, contentType, onProgress) {
    try {
      if (!("share" in navigator) || !("canShare" in navigator)) {
        return false;
      }

      onProgress?.("Fetching video for iOS...");
      // Fetch the file as a Blob (CORS enabled for Web Share API)
      const res = await fetch(downloadUrl, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();

      const file = new File([blob], filename, { type: contentType });

      if (!navigator.canShare({ files: [file] })) {
        // Some iOS builds expose navigator.share but not files support
        return false;
      }

      onProgress?.("Opening iOS save options...");
      await navigator.share({
        files: [file],
        title: "Save your recap video",
        text: "Tap 'Save Video' to add it to your Photos.",
      });

      onProgress?.("Shared. If you tapped 'Save Video', it's in Photos.");
      return true;
    } catch (err) {
      console.debug("Web Share (files) not available or failed:", err);
      return false;
    }
  }

  /**
   * iOS fallback: open URL in a new tab so user can Share → Save Video
   */
  tryOpenNewTab(downloadUrl, onProgress) {
    try {
      const win = window.open(downloadUrl, "_blank", "noopener,noreferrer");
      if (!win) {
        // Pop-up blocked
        return false;
      }
      onProgress?.('Video opened. In the new tab, tap the Share button and choose "Save Video".');
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Last-resort anchor approach (often ignored by iOS for media)
   */
  tryAnchorDownload(downloadUrl, filename, onProgress) {
    try {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.rel = "noopener";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onProgress?.("Attempted direct download. If nothing happened, use the Share → Save Video flow.");
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Android (Chrome et al.)
   */
  async downloadDirect(downloadUrl, filename, onProgress, onError) {
    try {
      onProgress?.("Preparing download for Android...");
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onProgress?.("Download started! Check your Downloads folder.");
      return true;
    } catch (err) {
      console.error("Android download error:", err);
      onError?.("Download failed. Please try again.");
      return false;
    }
  }

  /**
   * Desktop
   */
  async downloadDesktop(downloadUrl, filename, contentType, contentDisposition, onProgress, onError) {
    try {
      onProgress?.("Downloading...");
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onProgress?.("Download started!");
      return true;
    } catch (err) {
      console.error("Desktop download error:", err);
      onError?.("Download failed. Please try again.");
      return false;
    }
  }

  /**
   * Debug info
   */
  getPlatformInfo() {
    return {
      isIOS: this.isIOS,
      isAndroid: this.isAndroid,
      isMobile: this.isMobile,
      isSafari: this.isSafari,
      isIOSSafari: this.isIOSSafari,
      isIOSChrome: this.isIOSChrome,
      isIOSFirefox: this.isIOSFirefox,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      maxTouchPoints: navigator.maxTouchPoints
    };
  }
}

// Export singleton instance
export default new VideoDownloadService();
